import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, CircularProgress, InputAdornment, TextField, Typography } from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import TagsInput from "../../../components/common/TagsInput";
import RentalHistoryTab from "./rentalHistory/RentalHistoryTab";
import PropertyTabActionBar from "./PropertyTabActionBar";
import {
  apiCreateRentalHistory,
  apiDeleteRentalHistory,
  apiGetRentalHistories,
  apiUpdateRentalHistory,
} from "../../../graphQL/rentalHistory";
import {
  collectNoteTags,
  createBlankEntry,
  entryMatchesNoteFilter,
  isNoteFilterActive,
  normalizeEntry,
  normalizeNotes,
  sanitizeNotesForStorage,
  sanitizeRowsForStorage,
  toNumberOrNull,
  toTextOrEmpty,
} from "./rentalHistory/utils";
import { mockRentalHistoryEntries } from "./rentalHistory/mockData";

const myToast = {
  info: (msg) => console.info(msg),
  success: (msg) => console.log(msg),
  warning: (msg) => console.warn(msg),
  error: (msg) => console.error(msg),
};

const toEntryFromDoc = (doc) => {
  const payload = doc?.data || {};

  return normalizeEntry({
    id: doc?.id,
    surveyDate: doc?.surveyDate || null,
    occupancyPercent: toNumberOrNull(payload?.occupancyPercent),
    averageOccupancyPercent: toNumberOrNull(payload?.averageOccupancyPercent),
    notes: normalizeNotes(payload?.notes, doc?.createdAt || doc?.surveyDate),
    rows: Array.isArray(payload?.rows)
      ? payload.rows.map((row) => ({
          id: row?.id,
          units: toNumberOrNull(row?.units),
          unitType: toTextOrEmpty(row?.unitType),
          unitSizeSF: toNumberOrNull(row?.unitSizeSF),
          rentLow: toNumberOrNull(row?.rentLow),
          rentHigh: toNumberOrNull(row?.rentHigh),
          netRent: toNumberOrNull(row?.netRent),
          lowSF: toNumberOrNull(row?.lowSF),
          highSF: toNumberOrNull(row?.highSF),
        }))
      : [createBlankEntry().rows[0]],
  });
};

const toMutationInput = (propertyId, entry) => ({
  propertyId,
  surveyDate: entry?.surveyDate || "",
  data: {
    occupancyPercent: toNumberOrNull(entry?.occupancyPercent),
    averageOccupancyPercent: toNumberOrNull(entry?.averageOccupancyPercent),
    notes: sanitizeNotesForStorage(entry?.notes),
    rows: sanitizeRowsForStorage(entry?.rows),
  },
});

const fingerprintEntry = (entry, propertyId) =>
  JSON.stringify({
    propertyId,
    surveyDate: entry?.surveyDate || "",
    data: {
      occupancyPercent: toNumberOrNull(entry?.occupancyPercent),
      averageOccupancyPercent: toNumberOrNull(entry?.averageOccupancyPercent),
      notes: sanitizeNotesForStorage(entry?.notes),
      rows: sanitizeRowsForStorage(entry?.rows),
    },
  });

const hasSurveyDate = (entry) => Boolean(entry?.surveyDate && String(entry.surveyDate).trim());

const getEntriesSyncSignature = (entries, propertyId) =>
  JSON.stringify(
    (Array.isArray(entries) ? entries : []).map((entry) => ({
      id: entry?.id || "",
      fingerprint: fingerprintEntry(entry, propertyId),
    }))
  );

const PropertyRentalHistoryTab = ({ propertyInfo, onSaveStateChange }) => {
  const propertyId = propertyInfo?.id || "";
  const propertyName = propertyInfo?.name || "Property";

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [error, setError] = useState("");
  const [noteSearch, setNoteSearch] = useState("");
  const [noteTagFilter, setNoteTagFilter] = useState([]);

  const noteFilter = useMemo(() => ({ search: noteSearch, tags: noteTagFilter }), [noteSearch, noteTagFilter]);
  const tagOptions = useMemo(() => collectNoteTags(entries), [entries]);
  const visibleEntryCount = useMemo(
    () => entries.filter((entry) => entryMatchesNoteFilter(entry, noteFilter)).length,
    [entries, noteFilter]
  );

  const syncTimerRef = useRef(null);
  const syncInFlightRef = useRef(false);
  const pendingEntriesRef = useRef([]);
  const snapshotRef = useRef(new Map());

  const clearSyncTimer = () => {
    if (syncTimerRef.current) {
      clearTimeout(syncTimerRef.current);
      syncTimerRef.current = null;
    }
  };

  const loadEntries = useCallback(async () => {
    clearSyncTimer();
    syncInFlightRef.current = false;

    if (!propertyId) {
      const sampleEntries = mockRentalHistoryEntries.map(normalizeEntry);
      setEntries(sampleEntries);
      pendingEntriesRef.current = sampleEntries;
      snapshotRef.current = new Map();
      setError("");
      setIsDirty(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await apiGetRentalHistories({
        propertyId,
        ordering: { field: "surveyDate", order: "Desc" },
        pagination: { page: 1, limit: 250 },
      });

      const docs = response?.rentalHistories || [];
      const mapped = docs.map(toEntryFromDoc);

      const snapshot = new Map();
      mapped.forEach((entry) => {
        snapshot.set(entry.id, fingerprintEntry(entry, propertyId));
      });

      snapshotRef.current = snapshot;
      pendingEntriesRef.current = mapped;
      setEntries(mapped);
      setIsDirty(false);
    } catch (loadError) {
      console.error("Failed to load rental histories", loadError);
      setError("Could not load rental history records.");
      setEntries([]);
      snapshotRef.current = new Map();
      pendingEntriesRef.current = [];
      setIsDirty(false);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    loadEntries();
    return () => clearSyncTimer();
  }, [loadEntries]);

  const runSync = useCallback(async () => {
    if (!propertyId || syncInFlightRef.current) return;

    const currentEntries = (pendingEntriesRef.current || []).map(normalizeEntry);
    const startSignature = getEntriesSyncSignature(currentEntries, propertyId);

    syncInFlightRef.current = true;
    setSaving(true);

    try {
      const nextSnapshot = new Map(snapshotRef.current);
      const workingEntries = [...currentEntries];
      const currentIds = new Set();
      let skippedWithoutSurveyDate = 0;

      for (let index = 0; index < workingEntries.length; index += 1) {
        const entry = workingEntries[index];
        const existsOnServer = nextSnapshot.has(entry.id);

        if (!hasSurveyDate(entry)) {
          skippedWithoutSurveyDate += 1;
          if (existsOnServer) {
            currentIds.add(entry.id);
          }
          continue;
        }

        const nextFingerprint = fingerprintEntry(entry, propertyId);

        if (!existsOnServer) {
          const createdDoc = await apiCreateRentalHistory(toMutationInput(propertyId, entry));
          const createdEntry = toEntryFromDoc(createdDoc);
          workingEntries[index] = createdEntry;
          nextSnapshot.set(createdEntry.id, fingerprintEntry(createdEntry, propertyId));
          currentIds.add(createdEntry.id);
          continue;
        }

        currentIds.add(entry.id);

        if (nextSnapshot.get(entry.id) !== nextFingerprint) {
          const updatedDoc = await apiUpdateRentalHistory({
            id: entry.id,
            ...toMutationInput(propertyId, entry),
          });
          const updatedEntry = toEntryFromDoc(updatedDoc);
          workingEntries[index] = updatedEntry;
          nextSnapshot.set(updatedEntry.id, fingerprintEntry(updatedEntry, propertyId));
        }
      }

      const idsToDelete = [];
      nextSnapshot.forEach((_, id) => {
        if (!currentIds.has(id)) {
          idsToDelete.push(id);
        }
      });

      for (const id of idsToDelete) {
        await apiDeleteRentalHistory(id);
        nextSnapshot.delete(id);
      }

      snapshotRef.current = nextSnapshot;
      pendingEntriesRef.current = workingEntries;
      setEntries(workingEntries);

      if (skippedWithoutSurveyDate > 0) {
        setIsDirty(true);
        myToast.warning("Some snapshots were not saved. Survey Date is required.");
      } else {
        setIsDirty(false);
        myToast.success("Rental history saved.");
      }
    } catch (syncError) {
      console.error("Failed to persist rental history", syncError);
      myToast.error("Failed to save rental history changes.");
    } finally {
      syncInFlightRef.current = false;
      setSaving(false);

      const latestEntries = (pendingEntriesRef.current || []).map(normalizeEntry);
      const latestSignature = getEntriesSyncSignature(latestEntries, propertyId);
      if (latestSignature !== startSignature) {
        setIsDirty(true);
        clearSyncTimer();
        syncTimerRef.current = setTimeout(() => {
          runSync();
        }, 300);
      }
    }
  }, [propertyId]);

  const handleEntriesChange = useCallback((nextEntries) => {
    const normalized = (Array.isArray(nextEntries) ? nextEntries : []).map(normalizeEntry);
    setEntries(normalized);
    pendingEntriesRef.current = normalized;
    setIsDirty(true);
  }, []);

  const handleAddSurvey = useCallback(() => {
    setEntries((prevEntries) => {
      const nextEntries = [
        createBlankEntry(),
        ...(Array.isArray(prevEntries) ? prevEntries : []).map((entry) => normalizeEntry(entry)),
      ];
      pendingEntriesRef.current = nextEntries;
      return nextEntries;
    });
    setIsDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!propertyId) {
      myToast.info("Sample mode: save is available after selecting a saved property.");
      return;
    }
    clearSyncTimer();
    await runSync();
  }, [propertyId, runSync]);

  useEffect(() => {
    if (!onSaveStateChange) return;
    onSaveStateChange({
      active: isDirty,
      disabled: saving || !propertyId || !isDirty,
      loading: saving,
      onSave: handleSave,
      onAddSurvey: handleAddSurvey,
      title: "Upload to Cloud",
    });
  }, [onSaveStateChange, isDirty, saving, propertyId, handleSave]);

  useEffect(() => {
    return () => {
      onSaveStateChange?.(null);
    };
  }, [onSaveStateChange]);

  const statusLabel = useMemo(() => {
    if (loading) return "Loading rental history...";
    if (saving) return "Saving changes...";
    const count = isNoteFilterActive(noteFilter)
      ? `${visibleEntryCount} of ${entries.length}`
      : `${entries.length}`;
    if (!propertyId) {
      return `${count} sample survey snapshot${entries.length === 1 ? "" : "s"}`;
    }
    return `${count} survey snapshot${entries.length === 1 ? "" : "s"} - ${
      isDirty ? "Unsaved changes" : "Saved"
    }`;
  }, [entries.length, isDirty, loading, noteFilter, propertyId, saving, visibleEntryCount]);

  if (loading) {
    return (
      <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 1 }}>
      <PropertyTabActionBar
        showSave={false}
        leftContent={
          <Typography sx={{ fontSize: "12px", color: "#64748B", fontWeight: 600 }}>
            {statusLabel}
          </Typography>
        }
        saveActive={isDirty}
        saveDisabled={saving || !propertyId || !isDirty}
        saveLoading={saving}
        onSave={handleSave}
        saveTitle="Upload to Cloud"
        sx={{ mx: { xs: -1, md: -2 }, mb: 1 }}
      >
        <TextField
          size="small"
          placeholder="Search notes"
          value={noteSearch}
          onChange={(event) => setNoteSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: "#94A3B8" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: { xs: 140, md: 180 },
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#FFFFFF",
              borderRadius: "8px",
              fontSize: "12px",
              height: 30,
              "& fieldset": { borderColor: "#E2E8F0" },
            },
          }}
        />
        <TagsInput
          value={noteTagFilter}
          onChange={setNoteTagFilter}
          options={tagOptions}
          freeSolo={false}
          placeholder="Filter by tag"
          sx={{ width: { xs: 150, md: 220 } }}
        />
      </PropertyTabActionBar>


      {error ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      ) : null}

      <RentalHistoryTab
        propertyId={propertyId}
        propertyName={propertyName}
        entries={entries}
        onChange={handleEntriesChange}
        tagOptions={tagOptions}
        noteFilter={noteFilter}
      />
    </Box>
  );
};

export default PropertyRentalHistoryTab;
